import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {TextUtils} from "../../../../../shared/utils/text-utils";
import {IOrder} from "../orders.interface";
import {EcommerceService} from "../../ecommerce.service";
import {DbNumberFormatPipe} from "../../../../../shared/pipes/db-number-ceil.pipe";
import {filter, first} from "rxjs/operators";
import {DbResponse} from "../../../../../shared/interceptors/db-response";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {DbActiveModal} from "../../../../../modules/modal/modal-ref";
import {RefundData} from "../mapper/refund-data";
import * as fromStore from "../../../../../store";
import {Store} from "@ngrx/store";
import {RefundDataMapper} from "../mapper/refund-data-mapper";
import {combineLatest} from "rxjs";
import {TypeUtils} from "../../../../../shared/utils/type-utils";

@Component({
    selector: 'db-refund-items-popup',
    templateUrl: './refund-items-popup.component.html',
    styleUrl: './refund-items-popup.component.scss',
    standalone: false
})
export class RefundItemsPopupComponent implements OnInit {

  refundQuantities: number[];
  totalRefundValue: number = 0;
  refundAmount: number;
  refundsTotalSum: number = 0;
  refundedShippingAmount: number = 0;
  refundedShippingTaxAmount: number = 0;
  shippingAmount: number;
  shippingTax: number;
  calculatedRefundAmount: number = 0;
  calculatedTaxAmount: number = 0;
  itemRefundAmounts: number[] = [];
  itemRefundTaxes: number[] = [];
  itemTaxes: number[] = [];
  hasErrorsAmountIndexes: number[] = [];
  hasErrorsTaxIndexes: number[] = [];
  hasRefundErrors: boolean = false;
  hasShippingErrors: boolean = false;
  hasRefundErrorsWithTax: boolean = false;
  update = true;
  halfUp = new DbNumberFormatPipe()
  loading = false;
  updatedHeads: string[] = [
    TextUtils.getText('text_image'),
    TextUtils.getText('text_product'),
    TextUtils.getText('text_quantity'),
    TextUtils.getText('text_refundQuantity'),
    TextUtils.getText('text_price'),
    TextUtils.getText('text_refundAmount')
  ];
  heads: string[] = [
    TextUtils.getText('text_image'),
    TextUtils.getText('text_product'),
    TextUtils.getText('text_quantity'),
    TextUtils.getText('text_refundQuantity'),
    TextUtils.getText('text_price'),
    TextUtils.getText('text_refundAmount'),
    TextUtils.getText('text_tax'),
    TextUtils.getText('text_refundTax')
  ];
  headerGreedStyleRefund = {
    'grid-template-columns': '76px minmax(160px, 1fr) minmax(70px, 1fr) minmax(100px, 1fr) minmax(70px, 1fr) minmax(100px, 1fr) minmax(70px, 1fr) minmax(100px, 1fr)'
  };
  public Math: any = Math;

  @Input() order: IOrder;
  @Input() getAttributes: (meta: any[]) => string;
  @Input() websiteId: number;
  @Input() fullRefund: boolean;
  @Input() refunds?: RefundData[];
  @Input() onRefundSuccess?: () => void;

  @Output() updateChange = new EventEmitter<boolean>();
  @Output() resetEmitter = new EventEmitter<boolean>();

  constructor(
    private _ecommerceService: EcommerceService,
    private _activeModal: DbActiveModal,
    private _store: Store<fromStore.DbStoreState>,
  ) {
  }

  ngOnInit() {

    this.refundQuantities = Array(this.order.line_items.length).fill(0.00);
    this.totalRefundValue = +this.order?.total;

    let refundsTotalSum = 0;
    this.order?.refunds.forEach(item => {
      refundsTotalSum += parseFloat(item?.total);
    });

    this.refundsTotalSum = Math.abs(refundsTotalSum);

    this._getOrder();

    if (this.fullRefund) {
      this.order.line_items.forEach((item, index) => {
        this.handleQuantityChange(index, item?.quantity);
        this.itemRefundTaxes[index] = parseFloat(item?.total_tax);
      });
    }
  }

  private _getOrder() {
    combineLatest(
      this._store.select(fromStore.getEcommerceRefunds(this.websiteId, this.order?.id)),
      this._store.select(fromStore.getOrder(this.websiteId))
    )
      .pipe(
        filter(order => !!order),
        first()
      ).subscribe(([refundsData, orderData]) => {
      this.refunds = orderData?.refunds.map(refund => RefundDataMapper.map(refund));
      this.refundedShippingAmount = 0;
      this.refundedShippingTaxAmount = 0;

      let shippingRefundsTotalSum = 0.00;
      let shippingTaxRefundsTotalSum = 0.00;
      refundsData.forEach(refund => {
        refund.shipping_lines.forEach(shippingLine => {
          shippingRefundsTotalSum += this.halfUp.transform(shippingLine.total, 'halfUp', 2);
          shippingTaxRefundsTotalSum += this.halfUp.transform(shippingLine.total_tax, 'halfUp', 2);
        });
      });

      this.refundedShippingAmount = this.halfUp.transform(Math.abs(shippingRefundsTotalSum), 'halfUp', 2);
      this.refundedShippingTaxAmount = this.halfUp.transform(Math.abs(shippingTaxRefundsTotalSum), 'halfUp', 2);


      if (this.fullRefund) {
        this.shippingAmount = this.halfUp.transform(+this.order?.shipping_total - +this.refundedShippingAmount, 'halfUp', 2);
        this.shippingTax = this.halfUp.transform(+this.order?.shipping_tax - +this.refundedShippingTaxAmount, 'halfUp', 2);
      }

      const combinedLineItems = this.refunds.flatMap(order => order.lineItems);
      const combinedRefunds = this._combineRefundLineItems(combinedLineItems);
      this.order.line_items = this._combinedRefundsData(this.order.line_items, combinedRefunds);
    });
  }

  private _combineRefundLineItems(items) {
    const combinedItems = items.reduce((acc, item) => {
      // Find if the item already exists in the accumulator
      const existingItem = acc.find(i => (i.product_id === item.product_id && i.variation_id === item.variation_id));

      if (existingItem) {
        // Sum the values if the item already exists
        existingItem.quantity += item.quantity;
        existingItem.total = (parseFloat(existingItem.total) + parseFloat(item.total));
        existingItem.total_tax = (parseFloat(existingItem.total_tax) + parseFloat(item.total_tax)).toFixed(2);
      } else {
        // If it doesn't exist, use a parsed version of total and total_tax
        acc.push({
          product_id: +item?.product_id,
          variation_id: +item?.variation_id,
          quantity: +item?.quantity,
          total: parseFloat(item.total).toFixed(2),
          total_tax: parseFloat(item.total_tax).toFixed(2)
        });
      }
      return acc;
    }, []);
    return combinedItems;
  }

  private _combinedRefundsData(line_items, refunds): any {
    const combinedData = line_items.map(item => {
      const refund = refunds.find(refund => (refund?.product_id === item?.product_id && refund?.variation_id === item?.variation_id));
      return {
        ...item,
        refund_details: refund ? refund : null
      };
    });
    return combinedData;
  }

  handleQuantityChange(index: number, value: number) {
    this.refundQuantities[index] = value;
    this.recalculateForItem(index);
  }

  recalculateForItem(index: number) {
    const price = +this.order.line_items[index].price;
    const quantity = +this.refundQuantities[index];
    const tax_per_item = +this.order.line_items[index].total_tax / +this.order.line_items[index].quantity;

    // Calculate individual refund amounts and taxes using rounding
    this.calculatedRefundAmount = this.halfUp.transform(price * quantity, 'halfUp', 2);
    this.calculatedTaxAmount = this.halfUp.transform(tax_per_item * quantity, 'halfUp', 2);

    this.itemRefundAmounts[index] = this.calculatedRefundAmount;
    this.itemTaxes[index] = this.calculatedTaxAmount;
    this.itemRefundTaxes[index] = this.calculatedTaxAmount;

    this.hasRefundErrorsWithTax = (this.itemRefundAmounts[index] > this.calculatedRefundAmount) || (this.itemTaxes[index] > this.calculatedTaxAmount);

    // Recalculate totalRefundValue
    this.totalRefundValue = 0;
    for (let i = 0; i < this.itemRefundAmounts.length; i++) {
      this.totalRefundValue += this.halfUp.transform((this.itemRefundAmounts[i] ? this.itemRefundAmounts[i] : 0) + (this.itemTaxes[i] ? this.itemTaxes[i] : 0), 'halfUp', 2);
    }

    // Update refundAmount with the recalculated totalRefundValue
    this.refundAmount = this.totalRefundValue ? this.totalRefundValue : 0;
    this.validateRefundAmount('amount-with-tax');
  }

  changeTotalRefundAmout(hasRefundErrors, index, type): void {

    if (hasRefundErrors) {
      if (type === 'tax') {
        this.hasErrorsTaxIndexes.push(index);
      } else {
        this.hasErrorsAmountIndexes.push(index);
      }
    } else {
      if (type === 'tax') {
        const errorIndex = this.hasErrorsTaxIndexes.indexOf(index);
        if (errorIndex !== -1) {
          this.hasErrorsTaxIndexes.splice(errorIndex, 1);
        }
      } else {
        const errorIndex = this.hasErrorsAmountIndexes.indexOf(index);
        if (errorIndex !== -1) {
          this.hasErrorsAmountIndexes.splice(errorIndex, 1);
        }
      }
    }

    this._checkErrors();

    const totalSumAmounts = this.itemRefundAmounts.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    const totalSumTaxes = this.itemRefundTaxes.reduce((accumulator, currentValue) => accumulator + currentValue, 0);

    this.refundAmount = this.halfUp.transform(totalSumAmounts + totalSumTaxes, 'halfUp', 2);
  }

  private _checkErrors(): void {
    if (!TypeUtils.isEmpty(this.hasErrorsAmountIndexes) || !TypeUtils.isEmpty(this.hasErrorsTaxIndexes)) {
      this.hasRefundErrors = true;
    } else {
      this.hasRefundErrors = false;
    }
  }


  refundOrder() {
    this.loading = true;
    const shippingAmount = this.shippingAmount ? this.halfUp.transform(this.shippingAmount, 'halfUp', 2) : 0;
    const shippingTax = this.shippingTax ? this.halfUp.transform(this.shippingTax, 'halfUp', 2) : 0;
    const refundAmount = this.refundAmount ? this.halfUp.transform(this.refundAmount, 'halfUp', 2) : 0;
    const _amount = refundAmount + shippingAmount + shippingTax;
    const apiRefundPaymentMethods = ["bacs", "cod", "cheque", '']

    let data: any = {
      amount: _amount.toString(),
      api_restock: this.update,
      line_items: this.order.line_items.map((item, index) => {
          const data = {
            id: item.id,
            name: item.name,
            product_id: item.product_id,
            quantity: this.refundQuantities[index],
            refund_total: this.itemRefundAmounts[index],
            taxes: item?.taxes?.length ? [{id: item.taxes[0].id, refund_total: this.itemTaxes[index]}] : 0
          };

          if (item.taxes.length && this.itemRefundTaxes[index]) {
            data['refund_tax'] = [{
              id: item.taxes?.at(0)?.id,
              refund_total: this.itemRefundTaxes[index]
            }];
          }

          return data;
        }
      )
    };

    let shippingData = {};

    if (apiRefundPaymentMethods.includes(this.order.payment_method)) {
      data.api_refund = false;
    }

    if (this.shippingAmount || this.shippingTax) {
      shippingData = {
        id: this.order?.shipping_lines[0].id,
        refund_total: shippingAmount,
        refund_tax: []
      }

      if (this.order?.shipping_lines[0]?.taxes?.length > 0) {
        shippingData['refund_tax'].push({
          //id of shipping tax from shipping_lines
          id: this.order?.shipping_lines[0].taxes[0].id,
          refund_total: this.shippingTax
        });
      }
    }

    this._ecommerceService.createRefund(this.websiteId, this.order.id, data, shippingData)
      .pipe(filter((response: DbResponse): boolean => !!response),
        first())
      .subscribe((response: DbResponse): void => {
        this.loading = false;
        this._activeModal.close();
        if (response.success) {
          NotifierComponent.notify(new Notification('Refund created successfully', NotificationType.SUCCESS));
          this._store.dispatch(new fromStore.LoadEcommerceOrder({websiteId: this.websiteId, id: this.order?.id}));
          setTimeout(() => {
            this._store.dispatch(new fromStore.LoadEcommerceRefunds({
              websiteId: this.websiteId,
              orderId: this.order?.id
            }));
          }, 2000);
          if (typeof this.onRefundSuccess === 'function') {
            this.onRefundSuccess();
          }
        } else {
          NotifierComponent.notify(new Notification('Something Went Wrong...', NotificationType.DANGER));
        }
      });
  }

  validateRefundAmount(type: string) {
    const total = +this.order?.total;
    const shippingTotal = Number(this.order?.shipping_total);
    const shippingTax = Number(this.order?.shipping_tax);

    if (type === 'amount') {
      this.hasRefundErrors = isNaN(this.refundAmount) || this.refundAmount > this.totalRefundValue;
    } else if (type === 'ship_tax') {
      this.hasRefundErrors = this.shippingTax > shippingTax;
    } else if (type === 'amount-with-tax') {
      this.hasRefundErrors = this.totalRefundValue + this.shippingTax + this.shippingAmount > total;
    } else {
      this.hasRefundErrors = isNaN(this.shippingAmount) || this.shippingAmount > shippingTotal;
    }

    this._checkErrors();
  }

  onItemRefundAmountChange(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.itemRefundAmounts[index] = parseFloat(target.value) || 0;
    const price = +this.order.line_items[index].price || 0;
    const quantity = +this.order.line_items[index].quantity || 0;
    const refundedQuantity = Math.abs(+(this.order.line_items[index]?.refund_details?.quantity) || 0);
    const hasError = this.itemRefundAmounts[index] > (price * (quantity - refundedQuantity));
    this.changeTotalRefundAmout(hasError, index, 'amount');
  }

  onItemRefundTaxChange(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.itemRefundTaxes[index] = parseFloat(target.value) || 0;
    const totalTax = +this.order.line_items[index].total_tax || 0;
    const quantity = +this.order.line_items[index].quantity || 0;
    const refundedQuantity = Math.abs(+(this.order.line_items[index]?.refund_details?.quantity) || 0);
    const hasError = this.itemRefundTaxes[index] > (totalTax * (quantity - refundedQuantity));
    this.changeTotalRefundAmout(hasError, index, 'tax');
  }

  onShippingAmountChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.shippingAmount = parseFloat(target.value) || 0;
    this.validateRefundAmount('shipping');
  }

  onShippingTaxChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.shippingTax = parseFloat(target.value) || 0;
    this.validateRefundAmount('ship_tax');
  }

  onRefundAmountChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.refundAmount = parseFloat(target.value) || 0;
    this.validateRefundAmount('amount');
  }

  onCheckboxChange() {
    this.update = !this.update;
    this.updateChange.emit(this.update);
  }

}
